import { useState } from "react";
import { motion } from "motion/react";
import { CalendarDays, Clock, Users, Video, ExternalLink } from "lucide-react";
import TeacherMini from "./TeacherMini";
import SubjectEmoji from "./SubjectEmoji";
import type { ExamHubSession, ExamHubTeacher } from "../../../types/examHub";

const STATUS_CONFIG = {
  upcoming: {
    label: "Upcoming",
    color: "text-violet-700",
    bg: "bg-violet-50 border-violet-200",
  },
  live: {
    label: "LIVE NOW",
    color: "text-white",
    bg: "bg-red-500 border-red-500",
  },
  completed: {
    label: "Completed",
    color: "text-slate-500",
    bg: "bg-slate-100 border-slate-200",
  },
  recordings: {
    label: "Recordings",
    color: "text-orange-700",
    bg: "bg-orange-50 border-orange-200",
  },
};

export default function SessionCard({
  session,
  teacher,
  canAccess = true,
}: {
  session: ExamHubSession;
  teacher: ExamHubTeacher | undefined;
  canAccess?: boolean;
}) {
  const [teacherExpanded, setTeacherExpanded] = useState(false);
  const status = STATUS_CONFIG[session.status];
  const isUpcoming = session.status === "upcoming";
  const isRecordings = session.status === "recordings";
  const hasZoomLink = canAccess && Boolean(session.zoomLink.trim());

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col h-full"
    >
      <div
        className="h-1.5 w-full flex-shrink-0"
        style={{ backgroundColor: session.subjectColor }}
      />
      <div className="p-4 flex flex-col flex-1">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <SubjectEmoji
              subjectEmoji={session.subjectEmoji}
              subjectColor={session.subjectColor}
              className="w-8 h-8"
            />
            <div>
              <p
                className="text-[10px] uppercase tracking-widest"
                style={{ fontWeight: 700, color: session.subjectColor }}
              >
                {session.subject}
              </p>
              <p
                className="text-[13px] text-slate-900 leading-snug"
                style={{ fontWeight: 700 }}
              >
                {session.topic}
              </p>
            </div>
          </div>
          <span
            className={`flex-shrink-0 text-[10px] px-2.5 py-1 rounded-full border ${status.bg} ${status.color}`}
            style={{ fontWeight: 700 }}
          >
            {status.label}
          </span>
        </div>

        {/* Date / time / duration */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
            <span
              className="text-[12px] text-slate-600"
              style={{ fontWeight: 500 }}
            >
              {session.day}, {session.date}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            <span
              className="text-[12px] text-slate-600"
              style={{ fontWeight: 500 }}
            >
              {session.time}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-slate-400" />
            <span
              className="text-[12px] text-slate-600"
              style={{ fontWeight: 500 }}
            >
              {session.duration}
            </span>
          </div>
        </div>

        {/* Teacher expandable */}
        {teacher && (
          <div className="mb-4">
            <TeacherMini
              teacher={teacher}
              expanded={teacherExpanded}
              onExpand={() => setTeacherExpanded((e) => !e)}
            />
          </div>
        )}

        {/* Spacer pushes button to bottom */}
        <div className="flex-1" />

        {/* CTA — only for upcoming */}
        {isUpcoming && (
          <div className="flex flex-col gap-2">
            <button
              onClick={() =>
                hasZoomLink &&
                window.open(session.zoomLink, "_blank", "noopener,noreferrer")
              }
              disabled={!hasZoomLink}
              className={`w-full py-2.5 rounded-xl flex items-center justify-center gap-2 text-[12px] transition-all ${
                hasZoomLink
                  ? "text-white hover:opacity-90 cursor-pointer"
                  : "text-slate-500 bg-slate-100 cursor-not-allowed"
              }`}
              style={{
                backgroundColor: hasZoomLink ? session.subjectColor : undefined,
                fontWeight: 700,
              }}
            >
              {canAccess ? (
                <ExternalLink className="w-3.5 h-3.5" />
              ) : (
                <Video className="w-3.5 h-3.5" />
              )}{" "}
              {canAccess ? "Open Link" : "Not Registered"}
            </button>
            {canAccess && !session.zoomLink.trim() && (
              <p
                className="text-[11px] text-slate-500 text-center"
                style={{ fontWeight: 500 }}
              >
                Class details will be updated here after confirmation.
              </p>
            )}
          </div>
        )}
        {isRecordings && (
          <div className="flex flex-col gap-2">
            <button
              onClick={() =>
                hasZoomLink &&
                window.open(session.zoomLink, "_blank", "noopener,noreferrer")
              }
              disabled={!hasZoomLink}
              className={`w-full py-2.5 rounded-xl flex items-center justify-center gap-2 text-[12px] transition-all ${
                hasZoomLink
                  ? "text-white hover:opacity-90 cursor-pointer"
                  : "text-slate-500 bg-slate-100 cursor-not-allowed"
              }`}
              style={{
                backgroundColor: hasZoomLink ? session.subjectColor : undefined,
                fontWeight: 700,
              }}
            >
              <Video className="w-3.5 h-3.5" />{" "}
              {canAccess ? "View Recordings" : "Not Registered"}
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
